import { computed } from "vue";

import type { GraphDatabase } from "@ctrl/controller-rpc";

import {
  DEFAULT_TYPE_LABELS,
  type DefaultConsoleKind,
} from "@/components/console/default";
import {
  canCreateNewInConsole,
  getBuiltinKind,
  getTypeIcon,
} from "@/components/databases";
import * as inject from "@/components/inject";
import type { TypeDatabasePickerEntry } from "@/components/TypeDatabasePicker.vue";
import { useWorkspaceDatabaseGroups } from "@/composables/useWorkspaceDatabaseGroups";

/**
 * Entry id of a default type (Task/Event/Page) in the picker's "type" mode.
 * Callers marking their current selection (`selected-ids`) use it to point
 * at a default entry; custom types are keyed by their database id.
 */
export const defaultTypePickerId = (kind: DefaultConsoleKind): string =>
  `default:${kind}`;

/**
 * The user-creatable types for the `TypeDatabasePicker`, grouped by
 * organization/workspace (the picker renders the groups as section headers):
 * the default types (Task, Event, Page — Person is not creatable this way)
 * lead their workspace — the personal one, where their extensions databases
 * live — and custom types the user may create objects of follow, alphabetical
 * within each workspace. The personal organization always lists first.
 *
 * Both the console's type pill switcher and the sidebar's + New menu list
 * exactly these types.
 *
 * Must be called in a component `setup` (before any `await`): it injects the
 * profile and workspace databases. Returns a builder producing a fresh entry
 * list from the current state.
 */
export const useTypeDatabasePicker = (): (() => TypeDatabasePickerEntry[]) => {
  const profile = inject.profile();
  const workspaceDatabases = inject.workspaceDatabases();
  const workspaceGroups = useWorkspaceDatabaseGroups();

  // The default types' extensions databases (standing in for the types
  // themselves), live off the profile.
  const defaultDatabases = computed(() => profile.value.user.databases.default);

  return (): TypeDatabasePickerEntry[] => {
    const orgs = new Map(
      profile.value.organizations.map((org) => [org.id, org])
    );
    const groupOf = (
      workspaceId: string
    ): TypeDatabasePickerEntry["group"] | undefined => {
      const workspace = workspaceGroups.value.find(
        (entry) => entry.id === workspaceId
      );
      if (!workspace) return undefined;
      const org = orgs.get(workspace.orgId);
      return {
        orgId: workspace.orgId,
        orgName: org?.name ?? "Organization",
        workspaceId: workspace.id,
        workspaceName: workspace.name,
      };
    };
    const defaults: TypeDatabasePickerEntry[] = [];
    for (const kind of ["task", "event", "page"] as const) {
      const database = defaultDatabases.value[kind];
      if (database)
        defaults.push({
          id: defaultTypePickerId(kind),
          label: DEFAULT_TYPE_LABELS[kind],
          icon: getTypeIcon(profile.value, database),
          database,
          group: groupOf(database.workspace),
        });
    }
    // Defaults whose workspace the profile doesn't list (yet) still lead
    // the list, ungrouped.
    const entries: TypeDatabasePickerEntry[] = defaults.filter(
      (entry) => !entry.group
    );
    const seen = new Set<string>();
    const customEntry = (
      database: GraphDatabase,
      group?: TypeDatabasePickerEntry["group"]
    ): TypeDatabasePickerEntry | null => {
      if (seen.has(database.id)) return null;
      seen.add(database.id);
      // Default types are listed above; other built-ins are not creatable.
      if (getBuiltinKind(database)) return null;
      if (!canCreateNewInConsole(database)) return null;
      return {
        id: database.id,
        label: database.data_name || database.name,
        icon: getTypeIcon(profile.value, database),
        database,
        group,
      };
    };
    // The personal organization always leads; other organizations keep the
    // profile's order (stable sort).
    const orderedWorkspaces = [...workspaceGroups.value].sort(
      (a, b) =>
        Number(orgs.get(b.orgId)?.personal ?? false) -
        Number(orgs.get(a.orgId)?.personal ?? false)
    );
    for (const workspace of orderedWorkspaces) {
      const custom: TypeDatabasePickerEntry[] = [];
      for (const database of Object.values(workspace.databases)) {
        const entry = customEntry(database, groupOf(workspace.id));
        if (entry) custom.push(entry);
      }
      custom.sort((a, b) => a.label.localeCompare(b.label));
      entries.push(
        ...defaults.filter(
          (entry) => entry.group?.workspaceId === workspace.id
        ),
        ...custom
      );
    }
    // Databases from workspaces the profile doesn't list (or listed before
    // it resolves): keep them reachable, ungrouped, after the rest.
    const leftovers: TypeDatabasePickerEntry[] = [];
    for (const databases of Object.values(workspaceDatabases.value ?? {})) {
      for (const database of Object.values(databases)) {
        const entry = customEntry(database);
        if (entry) leftovers.push(entry);
      }
    }
    leftovers.sort((a, b) => a.label.localeCompare(b.label));
    return [...entries, ...leftovers];
  };
};
